import React, { useState, useEffect } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import DotColorPicker from "./DotColorPicker";
import Paywall from "./Paywall";
import NumberControl from "./NumberControl";
import DescriptionInput from "./DescriptionInput";

/**
 * Renders the NoteMetaData component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.setNoteBorderColor - The function to set the note border color.
 * @param {Function} props.setNoteFillColor - The function to set the note fill color.
 * @param {Function} props.setNoteTextColor - The function to set the note text color.
 * @param {Function} props.setNoteText - The function to set the note text.
 * @param {boolean} props.showFretNote - Indicates whether to show the fret note.
 * @param {Function} props.setShowFretNote - The function to set the show fret note flag.
 * @param {string} props.xcrfToken - The XCRF token.
 * @param {boolean} props.premium - Indicates whether the user is premium.
 * @param {string} props.orientation - The orientation of the component.
 * @param {boolean} props.isPremium - Indicates whether the user is premium.
 * @param {Function} props.setIsPremium - The function to set the is premium flag.
 * @param {boolean} props.isAnon - Indicates whether the user is anonymous.
 * @param {number} props.noteNumber - The note number.
 * @param {Function} props.setNoteNumber - The function to set the note number.
 * @param {boolean} props.autoIncrement - Indicates whether to auto increment the note number.
 * @param {Function} props.setAutoIncrement - The function to set the auto increment flag.
 * @param {boolean} props.showNumbers - Indicates whether to show the note numbers.
 * @param {Function} props.setShowNumbers - The function to set the show numbers flag.
 * @param {string} props.description - The description.
 * @param {Function} props.updateDescription - The function to update the description.
 * @param {Object} props.viewer - The viewer object.
 * @param {Function} props.redrawFretboard - The function to redraw the fretboard.
 * @param {Function} props.removeAllSelectedFrets - The function to remove all selected frets.
 * @returns {JSX.Element} The rendered NoteMetaData component.
 */
const NoteMetaData = ({
    setNoteBorderColor,
    setNoteFillColor,
    setNoteTextColor,
    setNoteText,
    showFretNote,
    setShowFretNote,
    xcrfToken,
    premium,
    orientation,
    isPremium,
    setIsPremium,
    isAnon,
    noteNumber,
    setNoteNumber,
    autoIncrement,
    setAutoIncrement,
    showNumbers,
    setShowNumbers,
    description,
    updateDescription,
    viewer,
    redrawFretboard,
    removeAllSelectedFrets
}) => {
    const [noteColor, setColor] = useState("#1A578E");
    const [noteFillColor, setFillColor] = useState("#1A578E");
    const [noteTextColor, setTextColor] = useState("#FFFFFF");
    const [colorPickerVisible, setColorPickerVisible] = useState(false);
    const [fillColorPickerVisible, setFillColorPickerVisible] = useState(false);
    const [textColorPickerVisible, setTextColorPickerVisible] = useState(false);
    const [noteSymbol, setNoteSymbol] = useState("");
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [_orientation, setOrientation] = useState("landscape");

    useEffect(() => {
        setOrientation(orientation);
    }, [orientation]);

    useEffect(() => {
        setNoteBorderColor(noteColor);
    }, [noteColor]);

    useEffect(() => {
        setNoteFillColor(noteFillColor);
    }, [noteFillColor]);

    useEffect(() => {
        setNoteTextColor(noteTextColor);
    }, [noteTextColor]);

    const handleSaveColorScheme = async () => {
        setOpen(true);
    };

    const handleColorClick = () => {
        setColorPickerVisible(!colorPickerVisible);
        setFillColorPickerVisible(false);
        setTextColorPickerVisible(false);
    };

    const handleFillColorClick = () => {
        setFillColorPickerVisible(!fillColorPickerVisible);
        setColorPickerVisible(false);
        setTextColorPickerVisible(false);
    };

    const handleTextColorClick = () => {
        setTextColorPickerVisible(!textColorPickerVisible);
        setColorPickerVisible(false);
        setFillColorPickerVisible(false);
    };

    const handleTextEvent = async (event) => {
        if (event.target.value !== "") {
            setNoteText(event.target.value);
            setNoteSymbol(event.target.value);
        } else {
            setNoteText("");
            setNoteSymbol("");
        }
    };

    const handleBorderColorEvent = async (newColor) => {
        setNoteBorderColor(newColor.hex);
        setColor(newColor.hex);
    };

    const handleFillColorEvent = async (newColor) => {
        setNoteFillColor(newColor.hex);
        setFillColor(newColor.hex);
    };

    const handleTextColorEvent = async (newColor) => {
        setNoteTextColor(newColor.hex);
        setTextColor(newColor.hex);
    };

    function handleToggleTextChange(event, value) {
        if (value !== "") {
            if (value === 'Note') {
                setShowFretNote(true);
                setNoteText("");
                setNoteSymbol(value);
            } else {
                setShowFretNote(false);
                setNoteText(value);
                setNoteSymbol(value);
            }

        } else {
            setNoteText("");
            setNoteSymbol("");
            setShowFretNote(false);
        }
    }

    const boxStyle = {
        display: "flex",
        color: "#ffffff",
        width: "100%",
        alignItems: "left",
    };

    const handleUpdatePremium = (boolVal) => {
        setIsPremium(boolVal);
        setOpen(boolVal);
    }


    const handleDialogClose = async () => {

        const nodeData = {
            "type": [{ "target_id": "fretboard_colorscheme" }],
            "title": [{ "value": title }],
            "field_note_border_color": [{ "value": noteColor }],
            "field_note_fill_color": [{ "value": noteFillColor }],
            "field_note_text_color": [{ "value": noteTextColor }]
        };
        await postNode(nodeData);
        setOpen(false);
    };

    const postNode = async (nodeData) => {
        try {
            const response = await fetch('/node?_format=json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': xcrfToken,
                },
                body: JSON.stringify(nodeData),
            });
            const data = await response.json();
            return data;
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Box
            id="metaData"
            sx={{ ...boxStyle }}>
            <Grid id="outer-container" container sx={{ width: "fit-content", flexDirection: { xs: "column", sm: "column" } }}>
                <ToggleButtonGroup
                    value={noteSymbol}
                    exclusive
                    onChange={handleToggleTextChange}
                    aria-label="Platform"
                    id="note-symbol"
                    size="small"
                    sx={{ paddingY: "5px" }}
                >
                    <ToggleButton value="T">T</ToggleButton>
                    <ToggleButton value="R">R</ToggleButton>
                    <ToggleButton value="X">X</ToggleButton>
                    <ToggleButton value="Note">Notes</ToggleButton>
                    <NumberControl
                        noteNumber={noteNumber}
                        setNoteNumber={setNoteNumber}
                        autoIncrement={autoIncrement}
                        setAutoIncrement={setAutoIncrement}
                        showNumbers={showNumbers}
                        setShowNumbers={setShowNumbers}
                    />
                </ToggleButtonGroup>
                <DotColorPicker
                    setColor={setColor}
                    setFillColor={setFillColor}
                    setTextColor={setTextColor}
                    xcrfToken={xcrfToken}
                    handleSaveColorScheme={handleSaveColorScheme}
                    noteColor={noteColor}
                    noteFillColor={noteFillColor}
                    noteTextColor={noteTextColor}
                    colorPickerVisible={colorPickerVisible}
                    fillColorPickerVisible={fillColorPickerVisible}
                    textColorPickerVisible={textColorPickerVisible}
                    handleBorderColorEvent={handleBorderColorEvent}
                    handleFillColorEvent={handleFillColorEvent}
                    handleTextColorEvent={handleTextColorEvent}
                    handleColorClick={handleColorClick}
                    handleFillColorClick={handleFillColorClick}
                    handleTextColorClick={handleTextColorClick}
                    open={open}
                />
                <DescriptionInput description={description} updateDescription={updateDescription} redrawFretboard={redrawFretboard} viewer={viewer} removeAllSelectedFrets={removeAllSelectedFrets} />

            </Grid>
            {isPremium ? (
                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>Enter a title for the color scheme</DialogTitle>
                    <DialogContent>
                        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={handleDialogClose}>Save</Button>
                    </DialogActions>
                </Dialog>
            ) : (
                <Paywall open={open} onClose={() => setOpen(true)} isPremium={isPremium} setIsPremium={handleUpdatePremium} isAnon={isAnon} />
            )}
        </Box >
    );
};

export default NoteMetaData;
